import React, { PropsWithChildren, useMemo, useState } from 'react'

import { getComponentOverrides } from '@/shared/styles/getComponentOverrides'
import { ColorModeContext, getDesignTokens } from '@/shared/styles/themes'
// eslint-disable-next-line import/no-extraneous-dependencies
import { BreakpointMap, RemScalingOptions } from '@atomixdesign/rem-scaling'
import { InlineFontSetter, RemScalingProvider } from '@atomixdesign/rem-scaling-react'
import { createTheme, Theme, ThemeProvider as MuiThemeProvider, useTheme } from '@mui/material'

export type GlobalThemeProviderProps = PropsWithChildren<{
  //
}>

export const useThemeBreakpoints = (): BreakpointMap => {
  const theme = useTheme()
  const { keys } = theme.breakpoints
  const breakpoints = theme.breakpoints.values

  return keys.reduce((carry, key, i) => {
    if (i <= keys.length - 1) {
      // @ts-ignore
      const prevBreakpoint = breakpoints[keys[i - 1]]

      if (prevBreakpoint) {
        // eslint-disable-next-line no-param-reassign
        carry[key] = [prevBreakpoint, breakpoints[key] - 1]
      }
    }

    return carry
  }, {} as BreakpointMap)
}

export const GlobalThemeProvider: React.FC<GlobalThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>('light')
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'))
      },
    }),
    [],
  )

  const theme: Theme | undefined = useMemo(() => {
    const baseTheme = createTheme(getDesignTokens(mode))
    const globalComponentOverwrite = getComponentOverrides(baseTheme)

    return createTheme(baseTheme, {
      components: globalComponentOverwrite,
    })
  }, [mode])

  const themeBreakpoints = useThemeBreakpoints()

  const remScaling: RemScalingOptions = {
    min: 14,
    max: theme.typography.fontSize,
    base: theme.typography.fontSize,
    breakpoints: themeBreakpoints,
    fallback: [0, 1600],
    nonce: process.env.nonce,
  }

  return (
    <ColorModeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        {/* @ts-ignore */}
        <RemScalingProvider options={remScaling}>
          <InlineFontSetter />
          {children}
        </RemScalingProvider>
      </MuiThemeProvider>
    </ColorModeContext.Provider>
  )
}
